import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import RiskHitSummaryContent from './RiskHitSummaryContent';
import { FraudMatchApplicant } from './riskHitSummaryApi';
import {
  SERIAL_FRAUD_TOOLTIP_TEXT,
  SERIAL_FRAUD_UNKNOWN_TOOLTIP_TEXT,
} from '../../utils/serialFraudDashboard';

type RiskFlagAlertProps = {
  isFraudRiskMatch: boolean | null;
  className?: string;
  applicant?: FraudMatchApplicant;
  displayVariant?: 'accordion' | 'icon';
};

const hasNonEmptyValue = (value?: string) => String(value || '').trim().length > 0;

const canLoadRiskSummary = (applicant: FraudMatchApplicant = {}) => {
  const hasName = hasNonEmptyValue(applicant.firstName) && hasNonEmptyValue(applicant.lastName);
  return (
    hasName ||
    hasNonEmptyValue(applicant.email) ||
    hasNonEmptyValue(applicant.phone) ||
    hasNonEmptyValue(applicant.ssn) ||
    hasNonEmptyValue(applicant.idDocumentNumber)
  );
};

const summaryHeaderSx = {
  bgcolor: '#F9BB04',
  color: '#1f1f1f',
  py: 0.75,
  px: 1.5,
  fontSize: 13,
  fontWeight: 600,
};

const summaryHeaderContentSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1,
};

const iconSx = {
  color: '#F9BB04',
  fontSize: 18,
  verticalAlign: 'middle',
};

const RiskFlagAlert = ({
  isFraudRiskMatch,
  className,
  applicant = {},
  displayVariant = 'accordion',
}: RiskFlagAlertProps) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const isSummaryAvailable = canLoadRiskSummary(applicant);

  if (isFraudRiskMatch !== true && isFraudRiskMatch !== null) {
    return null;
  }

  if (displayVariant === 'icon') {
    const tooltipText =
      isFraudRiskMatch === true
        ? SERIAL_FRAUD_TOOLTIP_TEXT
        : SERIAL_FRAUD_UNKNOWN_TOOLTIP_TEXT;

    return (
      <Tooltip title={tooltipText} arrow>
        <Box
          component='span'
          className={className}
          aria-label={tooltipText}
          tabIndex={0}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            cursor: 'default',
          }}
        >
          {isFraudRiskMatch === true ? (
            <WarningAmberIcon sx={iconSx} />
          ) : (
            <HelpOutlineIcon sx={{ ...iconSx, color: '#8a8a8a' }} />
          )}
        </Box>
      </Tooltip>
    );
  }

  if (isFraudRiskMatch === null) {
    return null;
  }

  if (!isSummaryAvailable) {
    return (
      <Box
        className={className}
        aria-label={SERIAL_FRAUD_TOOLTIP_TEXT}
        title={SERIAL_FRAUD_TOOLTIP_TEXT}
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: '#F9BB04',
          borderRadius: 1,
          overflow: 'hidden',
          ...summaryHeaderSx,
        }}
      >
        <Box component='div' sx={summaryHeaderContentSx}>
          <WarningAmberIcon sx={{ ...iconSx, mr: 0.75 }} />
          <span>{SERIAL_FRAUD_TOOLTIP_TEXT}</span>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={className} sx={{ width: '100%' }}>
      <Accordion
        expanded={isSummaryExpanded}
        onChange={(_, expanded) => setIsSummaryExpanded(expanded)}
        disableGutters
        elevation={0}
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: '#F9BB04',
          borderRadius: 1,
          overflow: 'hidden',
          '&::before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#1f1f1f' }} />}
          aria-controls='risk-summary-content'
          id='risk-summary-header'
          aria-label={SERIAL_FRAUD_TOOLTIP_TEXT}
          title={SERIAL_FRAUD_TOOLTIP_TEXT}
          sx={{
            ...summaryHeaderSx,
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-content > div': summaryHeaderContentSx,
          }}
        >
          <div>
            <WarningAmberIcon sx={{ ...iconSx, mr: 0.75 }} />
            <span>{SERIAL_FRAUD_TOOLTIP_TEXT}</span>
          </div>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            overflowX: 'auto',
          }}
        >
          <RiskHitSummaryContent applicant={applicant} isActive />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default RiskFlagAlert;
